// 🔧 role_guard.js
// ✅ Purpose: Page access guard based on user role, supporting global roles (admin/manager) and event-specific roles
// ✅ Features:
// - Auto-loads eventRole based on :eventslug in route
// - Redirects unauthorized users to /login or /dashboard

import { auth } from "./firebase";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useRole } from "./role_context";
import { useAuth } from "./auth_context";
import { useEventContext } from "./hooks/use_event_context";
import FullPageSpinner from "./components/FullPageSpinner";
import { logger } from './utils/logger';

const ALLOWED_UNVERIFIED_ROUTES = [
  "/dashboard",
  "/verify_email",
  "/resend_verification",
];

const RoleGuard = ({ allowedRoles = [], allowGuests = false, children }) => {

  const location = useLocation();
  const navigate = useNavigate();  
  
  const {
    role,
    roleLoading,
    eventRole,
    eventRoleLoading,
    loadEventRole,
    user,
  } = useRole();

  const normalizedRole =
    (role || "").toLowerCase();

  const normalizedEventRole =
    (eventRole || "").toLowerCase();

  // ✅ Manager and Judge are event-scoped authorization roles.
  // RoleGuard must load /events/{eventId}/users/{uid} whenever
  // the current route explicitly allows either role.
  const requiresEventRole =
    allowedRoles.includes("manager") ||
    allowedRoles.includes("judge");
  
  const { authLoading } = useAuth();
  const { eventslug } = useParams();

  const {
    eventId,
    eventLoading,
  } = useEventContext({
    redirectIfNotFound: false,
  });

  const [hasCheckedEventRole, setHasCheckedEventRole] = useState(false);
  const [isUnauthorized, setIsUnauthorized] = useState(false);
  const [checkingEmailVerification, setCheckingEmailVerification] = useState(false);

  useEffect(() => {
    const run = async () => {
      if (
        user &&
        eventId &&
        requiresEventRole &&
        !hasCheckedEventRole
      ) {
        try {
          logger.log("🔐 RoleGuard loading event role...", {
            eventId,
          });

          await loadEventRole(eventId);
        } catch (err) {
          logger.error("❌ RoleGuard failed loading event role:", {
            value: err,
            eventId,
          });
        } finally {
          // ✅ Critical mobile spinner fix:
          // ✅ even if role lookup fails, do not wait forever.
          setHasCheckedEventRole(true);
        }
      }
    };

    run();
  }, [user, eventId, requiresEventRole, hasCheckedEventRole, loadEventRole]);

  useEffect(() => {
    // ✅ If user or event changes, reset the event-role check.
    setHasCheckedEventRole(false);
    setIsUnauthorized(false);
  }, [user?.uid, eventId]);

  useEffect(() => {
    // 🔐 Redirect if user is not logged in and guests are NOT allowed
    if (!user && !authLoading && !allowGuests) {
      logger.warn("🔐 User not logged in — redirecting to login", {});
      navigate(`/login?redirect=${location.pathname}`, { replace: true });
      return;
    }

    // 📬 Redirect if email is not verified
    if (user && !authLoading && !user.emailVerified) {
      const isAllowedRoute = ALLOWED_UNVERIFIED_ROUTES.some((route) =>
        location.pathname.startsWith(route)
      );

      if (isAllowedRoute) {
        return;
      }

      const refreshAndCheckEmailVerification = async () => {
        try {
          setCheckingEmailVerification(true);

          if (auth.currentUser) {
            await auth.currentUser.reload();
            await auth.currentUser.getIdToken(true);

            logger.log("📩 RoleGuard refreshed email verification:", {
              emailVerified: auth.currentUser.emailVerified,
            });

            if (auth.currentUser.emailVerified) {
              return;
            }
          }

          navigate("/dashboard?verifyRequired=1", {
            replace: true,
          });
        } catch (err) {
          logger.error("❌ RoleGuard failed to refresh email verification:", {
            value: err,
          });

          navigate("/dashboard?verifyRequired=1", {
            replace: true,
          });
        } finally {
          setCheckingEmailVerification(false);
        }
      };

      refreshAndCheckEmailVerification();
      return;
    }

  }, [user, allowGuests, authLoading, navigate, location.pathname]);

  useEffect(() => {
    if (
      !authLoading &&
      !roleLoading &&
      !eventLoading &&
      !eventRoleLoading &&
      !allowGuests &&
      user &&
      (hasCheckedEventRole || !requiresEventRole) &&
      !(
        normalizedRole === "admin" ||
        (allowedRoles.includes("manager") && normalizedEventRole === "manager") ||
        (allowedRoles.includes("judge") && normalizedEventRole === "judge") ||
        (
          !["manager", "judge"].includes(normalizedRole) &&
          allowedRoles.includes(normalizedRole)
        )
      )
    ) {
      logger.warn("🚫 Unauthorized: redirecting to /dashboard", {});
      setIsUnauthorized(true);
      navigate("/dashboard?rg", { replace: true });
    }
  }, [
    user,
    role,
    eventRole,
    authLoading,
    roleLoading,
    eventLoading,
    eventRoleLoading,
    hasCheckedEventRole,
    allowGuests,
    allowedRoles,
    normalizedEventRole,
    normalizedRole,
    requiresEventRole,
    navigate,
  ]);

  if (
    authLoading ||
    roleLoading ||
    eventLoading ||
    checkingEmailVerification ||
    (requiresEventRole && eventslug && (!hasCheckedEventRole || eventRoleLoading))
  ) {
    logger.log("⏳ ROLE GUARD WAIT", {
        authLoading,
        roleLoading,
        eventLoading,
        eventRoleLoading,
        hasCheckedEventRole,
        checkingEmailVerification,
        eventslug,
        eventId,
        role,
        eventRole,
    });

    return <FullPageSpinner message="Checking Role Permissions..." />;
  }

  if (allowGuests && !user) return children;
  if (normalizedRole === "admin") return children;
  if (allowedRoles.includes("manager") && normalizedEventRole === "manager") return children;
  if (allowedRoles.includes("judge") && normalizedEventRole === "judge") return children;
  if (
    user &&
    !["manager", "judge"].includes(normalizedRole) &&
    allowedRoles.includes(normalizedRole)
  ) return children;
  if (isUnauthorized) return null;

  return <div className="container mt-5 text-danger">❌ Something went wrong.</div>;
};

export default RoleGuard;
